/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { getListingVariants } from './getListingVariants';
import resolveSalesChannelId from './helpers/resolveSalesChannelId';
import { getListingChannelId } from './helpers/channelHelpers';
import { showPublishedToChannelToast } from './helpers/showPublishedToChannelToast';
import handlePublishListingFailures from './helpers/handlePublishListingFailures';
import handlePublishVariantMediaWarnings from './helpers/handlePublishVariantMediaWarnings';
import { showPublishFailedDialog } from '../../components/ProductInfo/PublishFailedDialog';
import l10n from '../../shared/strings';
import {
  buildListingMediaPayload,
  resolveMediaMasterVariantIds,
  shouldIncludeDraftMedia,
} from './helpers/buildDraftMediaPayload';
import refreshListingAssetsForChannels from './helpers/refreshListingAssetsForChannels';
import { stageListingProductsFromCreateDraft } from './stageListingProductsFromCreateDraft';

export const createAndPublishListingFromMaster = createAsyncThunk(
  'products/createAndPublishListingFromMaster',
  async ({
    productId,
    channelIds,
    masterVariantIds,
    draftMedia = {},
    overrideExisting = false,
  }, { dispatch, getState, extra: { sdk, Toast } }) => {
    try {
      const state = getState();
      const { user: { team } } = state;
      const product = state.products.items.find(({ _id }) => _id === productId);
      const salesChannelIds = channelIds.map((channelId) => resolveSalesChannelId(channelId, team?.salesChannels));
      const includeDraftMedia = shouldIncludeDraftMedia(draftMedia.publishingScope);
      const includeGallery = includeDraftMedia && !draftMedia.isVariantSync;
      const mediaMasterVariantIds = includeDraftMedia
        ? resolveMediaMasterVariantIds({
          masterVariantIds,
          product,
          variantMediaByChannel: draftMedia.variantMediaByChannel,
          variantMediaStepCompleted: draftMedia.variantMediaStepCompleted,
        })
        : null;
      const listingMedia = includeDraftMedia
        ? buildListingMediaPayload({
          channelIds,
          productMediaByChannel: draftMedia.productMediaByChannel,
          variantMediaByChannel: draftMedia.variantMediaByChannel,
          salesChannels: team?.salesChannels,
          masterVariantIds: mediaMasterVariantIds,
          includeGallery,
        })
        : [];
      const publishingScope = {
        text: draftMedia.publishingScope?.text !== false,
        media: includeDraftMedia,
      };
      const { data: publishData } = await sdk.products.publishDirectly({
        masterProductId: productId,
        salesChannelIds,
        ...(masterVariantIds?.length && { masterVariantIds }),
        ...(listingMedia.length && { listingMedia }),
        publishingScope,
      });

      if (!publishData.published?.length && !publishData.affectedListings?.length) {
        throw new Error('Listings not created');
      }

      const publishedById = new Map(
        (publishData.published || []).map((listing) => [String(listing._id), listing]),
      );
      const listingProducts = (publishData.affectedListings || []).map((listing) => ({
        channelId: getListingChannelId(listing),
        listingProduct: publishedById.get(String(listing._id)) || listing,
        fromCreateDraft: false,
      }));

      dispatch(stageListingProductsFromCreateDraft({ productId, listingProducts }));

      await Promise.all(listingProducts.map(({ listingProduct }) => dispatch(getListingVariants({
        masterProductId: productId,
        channelId: getListingChannelId(listingProduct),
        listingProductId: listingProduct._id,
      })).unwrap()));

      if (publishData.failed?.length) {
        handlePublishListingFailures(publishData.failed, { Toast, showPublishFailedDialog });
        if (!publishData.published?.length) {
          const publishError = new Error(publishData.failed[0]?.error?.message || 'Publishing failed');
          publishError.publishFailuresHandled = true;
          throw publishError;
        }
      }

      if (channelIds.length === 1) {
        showPublishedToChannelToast({
          Toast,
          channelId: channelIds[0],
          salesChannels: team?.salesChannels || [],
        });
      } else if (channelIds.length > 1) {
        showPublishedToChannelToast({
          Toast,
          channelIds,
          salesChannels: team?.salesChannels || [],
        });
      }

      handlePublishVariantMediaWarnings(publishData.published, { showPublishFailedDialog });

      const listings = listingProducts.map(({ channelId, listingProduct }) => ({
        channelId,
        listingProductId: listingProduct._id,
      }));

      await refreshListingAssetsForChannels({
        dispatch,
        getState,
        productId,
        listings,
      });

      return { productId, listingProducts };
    } catch (error) {
      if (!error.publishFailuresHandled) {
        Toast(l10n.TOAST.PUBLISHING_SERVER_ERROR, {
          type: 'error',
          autoClose: false,
          btnOkValue: l10n.TOAST.PUBLISHING_TRY_AGAIN,
          onOk: () => dispatch(createAndPublishListingFromMaster({
            productId,
            channelIds,
            masterVariantIds,
            draftMedia,
            overrideExisting,
          })),
        });
      }
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(createAndPublishListingFromMaster.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(createAndPublishListingFromMaster.fulfilled, (state) => {
      state.isBusy = false;
    })
    .addCase(createAndPublishListingFromMaster.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot publish listing directly';
    });
};
